import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { ConflictNotice } from '../ui/ConflictNotice';
import { PAYMENTS_KEY, PAYMENTS_SETTINGS_RESOURCE } from './types';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';

type Settings = { reminderDays: number[]; reminderEmailSubject: string; reminderEmailBody: string; autoBook: boolean; version: number };

/** "1, 7, 14" ↔ [1, 7, 14]; null when something is not a whole number of days. */
export function parseDays(text: string): number[] | null {
  const parts = text
    .split(/[\s,;]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.every((p) => /^\d+$/.test(p))) return null;
  return [...new Set(parts.map(Number))].sort((a, b) => a - b);
}

/** "Finanse → Ustawienia → Płatności" (F36): the days after the due date for reminders and the client e-mail template. */
export function PaymentsSettings({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: [...PAYMENTS_KEY, 'settings'], queryFn: () => api<Settings>('/api/payments/settings') });
  const [days, setDays] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [autoBook, setAutoBook] = useState(true);
  const [local, setLocal] = useState('');
  const [saved, setSaved] = useState(false);
  const s = settings.data;
  useEffect(() => {
    if (s) {
      setDays(s.reminderDays.join(', '));
      setSubject(s.reminderEmailSubject);
      setBody(s.reminderEmailBody);
      setAutoBook(s.autoBook);
    }
  }, [s]);
  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api('/api/payments/settings', { method: 'PATCH', body: { ...patch, version: s!.version } }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: [...PAYMENTS_KEY, 'settings'] });
    },
  });
  const editable = can(me, PAYMENTS_SETTINGS_RESOURCE, 'edit');
  /** The changes, or null with the reason shown. */
  function changes(): Record<string, unknown> | null {
    if (!s) return null;
    const reminderDays = parseDays(days);
    if (!reminderDays || reminderDays.some((d) => d < 1 || d > 365)) {
      setLocal('Dni przypomnień: liczby od 1 do 365, np. 1, 7, 14');
      return null;
    }
    setLocal('');
    setSaved(false);
    return {
      ...(reminderDays.join(',') !== s.reminderDays.join(',') ? { reminderDays } : {}),
      ...(subject !== s.reminderEmailSubject ? { reminderEmailSubject: subject } : {}),
      ...(body !== s.reminderEmailBody ? { reminderEmailBody: body } : {}),
      ...(autoBook !== s.autoBook ? { autoBook } : {}),
    };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const patch = changes();
    if (patch && Object.keys(patch).length > 0) save.mutate(patch);
  }
  // owner 2026-10-23 21:14: leaving with unsaved settings asks first
  const guardRef = useUnsavedGuard({
    dirty: editable && !!s && !sameValues({ days: days.replace(/\s/g, ''), subject, body, autoBook }, { days: s.reminderDays.join(','), subject: s.reminderEmailSubject, body: s.reminderEmailBody, autoBook: s.autoBook }),
    save: async () => {
      const patch = changes();
      if (!patch) return false;
      if (Object.keys(patch).length > 0) await save.mutateAsync(patch);
      return true;
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;
  const error = local || (save.error && !conflict ? (save.error instanceof ApiError ? (fieldIssues(save.error)[0]?.message ?? save.error.message) : 'Nie udało się zapisać') : '');
  return (
    <section aria-label="Płatności — ustawienia" data-feature-id="payments.settings">
      <h2>Płatności</h2>
      <p className="lede">Księgowanie wpłat z wyciągu, przypomnienia o zaległych należnościach i e-mail do klienta.</p>
      {settings.error && <p className="alert" role="alert">{settings.error instanceof ApiError ? settings.error.message : 'Nie udało się wczytać ustawień'}</p>}
      {s && (
        <form ref={guardRef} className="form fin-settings" aria-label="Ustawienia płatności" onSubmit={submit}>
          {conflict && (
            <ConflictNotice
              onReload={() => {
                save.reset();
                void settings.refetch();
              }}
            />
          )}
          <label className="check" data-feature-id="payments.auto_book">
            <input type="checkbox" checked={autoBook} onChange={(e) => setAutoBook(e.target.checked)} disabled={!editable} /> Księguj wpłaty z wyciągu automatycznie, gdy tytuł ma numer faktury, a kwota się zgadza
          </label>
          <label className="field">
            <span>Przypomnienia — dni po terminie</span>
            <input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} disabled={!editable} placeholder="1, 7, 14" />
          </label>
          <label className="field">
            <span>Temat e-maila do klienta</span>
            <input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} disabled={!editable} />
          </label>
          <label className="field">
            <span>Treść e-maila do klienta</span>
            <textarea rows={11} value={body} onChange={(e) => setBody(e.target.value)} disabled={!editable} />
          </label>
          <p className="muted small">Pola: {'{klient}'}, {'{kwota}'}, {'{termin}'}, {'{numer}'}, {'{rachunek}'}.</p>
          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}
          {saved && <p role="status">Zapisano.</p>}
          {editable && (
            <div className="actions">
              <button className="btn primary" type="submit" disabled={save.isPending}>
                Zapisz
              </button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
